import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, API } from "@/lib/api";
import { AdminNav } from "@/components/AdminNav";
import {
  AlertTriangle, Clock, Phone, MapPin, Wallet, FileText, Paperclip, ClipboardList,
  CheckCircle2, Inbox, Globe, MessageSquare, CalendarClock, ChevronRight, Flag, StickyNote, Reply,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  Submitted: "bg-accent text-accent-foreground",
  Cancelled: "bg-muted text-muted-foreground",
  customer_cancelled: "bg-red-100 text-red-700",
  Completed: "bg-primary text-primary-foreground",
};
const adminStatusLabel = (s) => (s === "customer_cancelled" ? "Customer Cancelled" : s);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState(["All"]);
  const [stats, setStats] = useState({ total: 0, urgent_active: 0, new: 0, completed: 0, unread_messages: 0 });
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [lastCount, setLastCount] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async (silent = false) => {
    try {
      const [q, s] = await Promise.all([
        adminApi.get(`/admin/quotes?filter=${encodeURIComponent(filter)}&q=${encodeURIComponent(search)}`),
        adminApi.get("/admin/stats"),
      ]);
      if (lastCount !== null && filter === "All" && q.data.length > lastCount && silent) {
        toast.success("New quote request received!", { duration: 5000 });
      }
      if (filter === "All") setLastCount(q.data.length);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("mrshu_admin_token");
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [nav, lastCount, filter, search]);

  useEffect(() => {
    if (!localStorage.getItem("mrshu_admin_token")) { nav("/admin/login"); return; }
    adminApi.get("/meta/options").then(({ data }) => { setStatuses(data.statuses); setFilters(data.admin_filters); }).catch(() => {});
  }, [nav]);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 6000);
    return () => clearInterval(id);
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Status updated to "${status}"`);
    } catch { toast.error("Could not update status"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNav active="requests" />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Stat icon={<Inbox className="h-5 w-5" />} label="Total" value={stats.total} testId="stat-total" />
          <Stat icon={<AlertTriangle className="h-5 w-5" />} label="Urgent active" value={stats.urgent_active} testId="stat-urgent" urgent />
          <Stat icon={<ClipboardList className="h-5 w-5" />} label="New" value={stats.new} testId="stat-new" />
          <Stat icon={<MessageSquare className="h-5 w-5" />} label="Unread msgs" value={stats.unread_messages} testId="stat-unread" />
          <Stat icon={<Reply className="h-5 w-5" />} label="Awaiting reply" value={stats.awaiting_reply} testId="stat-awaiting" amber />
          <Stat icon={<Flag className="h-5 w-5" />} label="Follow up" value={stats.follow_up} testId="stat-followup" flag />
          <Stat icon={<CheckCircle2 className="h-5 w-5" />} label="Completed" value={stats.completed} testId="stat-completed" />
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filters">
          {filters.map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-white border border-border text-foreground hover:border-primary/40"}`}>
              {f}{f === "Customer Cancelled" && stats.customer_cancelled > 0 ? ` (${stats.customer_cancelled})` : ""}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, reference or registration plate…"
            className="w-full max-w-md rounded-full border border-border bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-20">Loading requests…</p>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No requests to show.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {quotes.map((q) => <QuoteCard key={q.id} q={q} statuses={statuses} onStatus={changeStatus} nav={nav} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ icon, label, value, testId, urgent, amber, flag }) {
  const hot = value > 0 && (urgent || amber || flag);
  const tone = urgent ? "border-destructive bg-[#FFEDEF] text-destructive"
    : amber ? "border-amber-400 bg-amber-50 text-amber-700"
    : flag ? "border-red-400 bg-red-50 text-red-600" : "";
  return (
    <div data-testid={testId} className={`rounded-2xl border p-5 ${hot ? tone : "border-border bg-white"}`}>
      <div className={`flex items-center gap-2 text-sm font-medium ${hot ? "" : "text-muted-foreground"}`}>{icon} {label}</div>
      <p className={`font-display text-3xl font-bold mt-2 ${hot ? "" : "text-primary"}`}>{value}</p>
    </div>
  );
}

function QuoteCard({ q, statuses, onStatus, nav }) {
  const token = localStorage.getItem("mrshu_admin_token");
  const urgent = q.urgency === "Urgent";
  const submitted = new Date(q.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  const appt = q.appointment;

  return (
    <div data-testid={`quote-card-${q.ref_number}`}
      className={`rounded-3xl border p-6 shadow-sm animate-fade-in ${q.follow_up ? "border-red-500 border-2 ring-2 ring-red-300 bg-white" : urgent ? "border-destructive border-2 bg-[#FFEDEF]" : "border-border bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${urgent ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
            {urgent ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />} {q.urgency}
          </div>
          {q.follow_up && <span data-testid={`followup-flag-${q.ref_number}`} className="flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-1 text-xs font-bold"><Flag className="h-3 w-3" /> Follow Up</span>}
        </div>
        <span className="font-display text-sm font-bold text-primary">{q.ref_number}</span>
      </div>

      <h3 className="font-display text-lg font-bold text-foreground mt-3">{q.name}</h3>
      <p className="text-sm font-medium text-primary">{q.work_type}
        {q.vehicle && <span data-testid={`mobile-badge-${q.ref_number}`} className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Mobile Service</span>}
      </p>

      {q.vehicle && (
        <div data-testid={`vehicle-info-${q.ref_number}`} className="mt-3 rounded-xl border border-primary/20 bg-secondary/40 p-3 text-xs text-foreground space-y-0.5">
          <p className="font-bold text-primary">Vehicle · {q.vehicle.plate}</p>
          <p>Fuel: {q.vehicle.fuel_type}{q.vehicle.odometer_km ? ` · ${q.vehicle.odometer_km} km` : ""}</p>
          {q.vehicle.make_model_year && <p>{q.vehicle.make_model_year}</p>}
          <p>Location: {q.address}</p>
          {q.vehicle.parking_notes && <p>Access: {q.vehicle.parking_notes}</p>}
          <p>Spark-plug quote: {q.vehicle.spark_plug_quote ? "Yes" : "No"}</p>
        </div>
      )}

      <div className="mt-4 space-y-2 text-sm text-foreground">
        <Line icon={<Phone className="h-4 w-4 text-primary/70" />}><a href={`tel:${q.phone}`} className="hover:underline">{q.phone}</a></Line>
        <Line icon={<MapPin className="h-4 w-4 text-primary/70" />}>{q.address}</Line>
        <Line icon={<Clock className="h-4 w-4 text-primary/70" />}>{q.preferred_time}</Line>
        {q.budget && <Line icon={<Wallet className="h-4 w-4 text-primary/70" />}>Budget: {q.budget}</Line>}
        {q.description && <Line icon={<FileText className="h-4 w-4 text-primary/70" />}>{q.description}</Line>}
        <Line icon={<Paperclip className="h-4 w-4 text-primary/70" />}>{q.media?.length || 0} file(s) uploaded</Line>
        {appt && <Line icon={<CalendarClock className="h-4 w-4 text-primary/70" />}>{appt.type} · {appt.state}{appt.date ? ` · ${appt.date} ${appt.slot || ""}` : ""}</Line>}
        <Line icon={<Globe className="h-4 w-4 text-primary/70" />}><span data-testid={`language-${q.ref_number}`}>Language: {q.language_label || "English"}</span></Line>
      </div>

      {q.media?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`media-${q.ref_number}`}>
          {q.media.map((m) => m.kind === "video" ? (
            <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer" className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary text-primary-foreground text-xs">Video</a>
          ) : (
            <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer">
              <img src={`${API}/files/${m.id}?auth=${token}`} alt={m.filename} className="h-16 w-16 rounded-lg object-cover border border-border" />
            </a>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4">Submitted {submitted}</p>

      {q.status === "customer_cancelled" && (
        <div data-testid={`cancelled-banner-${q.ref_number}`} className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          <p className="font-bold">Cancelled by customer · 客户主动取消</p>
          <p>{q.cancelled_at ? new Date(q.cancelled_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }) : ""}</p>
          {q.cancellation_reason && <p>Reason: {q.cancellation_reason}</p>}
          {q.previous_status && <p>Previous status: {q.previous_status}</p>}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[q.status] || "bg-secondary text-secondary-foreground"}`}>{adminStatusLabel(q.status)}</span>
        {q.unread_messages > 0 && (
          <span data-testid={`unread-${q.ref_number}`} className="flex items-center gap-1 rounded-full bg-destructive px-2.5 py-1 text-xs font-bold text-destructive-foreground">
            <MessageSquare className="h-3 w-3" /> {q.unread_messages}
          </span>
        )}
        {q.awaiting_reply && (
          <span data-testid={`awaiting-${q.ref_number}`} className="flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-bold text-white">
            <Reply className="h-3 w-3" /> Awaiting reply
          </span>
        )}
        {q.note_count > 0 && (
          <button data-testid={`note-indicator-${q.ref_number}`} onClick={() => nav(`/admin/jobs/${q.id}#notes`)}
            title={q.last_note ? q.last_note.text : ""}
            className="flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold border border-amber-300">
            <StickyNote className="h-3 w-3" /> {q.note_count}
            {q.last_note && <span className="hidden lg:inline max-w-[120px] truncate">· {q.last_note.text}</span>}
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <select data-testid={`status-select-${q.ref_number}`} value={q.status} onChange={(e) => onStatus(q.id, e.target.value)}
          className="flex-1 rounded-xl border border-input bg-white px-2 py-2 text-xs font-medium focus:ring-2 focus:ring-primary focus:outline-none">
          {!statuses.includes(q.status) && <option value={q.status}>{adminStatusLabel(q.status)}</option>}
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <button data-testid={`open-job-${q.ref_number}`} onClick={() => nav(`/admin/jobs/${q.id}`)}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold active:scale-95 transition-transform">
        Open job page <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function Line({ icon, children }) {
  return <div className="flex items-start gap-2">{icon}<span className="flex-1">{children}</span></div>;
}
